import { z } from 'zod'
import {
  CheckoutProductDisplay,
  CheckoutSessionView,
  MeEntitlementSnapshot,
  RestoreReconciliation,
} from './api.js'
import { DeliveryMode, PurchaseProvider, PurchaseType, RoutingMode } from './enums.js'

/**
 * Billing recognition, restore and policy-permitted checkout — SM6 (B12, B13, B21.4.4; MCP-AC-024..030,
 * 059..062, 124; PUR-15 analog).
 *
 * Three rules are shapes here rather than promises, because each one is a thing that goes wrong in
 * the wild:
 *
 * 1. **No shape in this file can carry card data.** There is no field for a PAN, a CVC, an expiry or
 *    a provider payload anywhere — a host that tries to send one is refused by `.strict()` before a
 *    handler runs, and the checkout answer names the provider's hosted page as the only surface a
 *    payment credential is ever entered on (MCP-AC-059). The absence is asserted, not promised: the
 *    S5.2 regression test renders every schema this package exports to JSON Schema and fails on any
 *    property whose name is a card-like token, so this file cannot grow one.
 * 2. **A checkout answer is a hosted URL and a state, never a grant.** `entitlement_granted` is the
 *    literal `false` on the session result: only provider verification grants, and a redirect, a
 *    thank-you page or an open tab never does (MCP-AC-060).
 * 3. **An upgrade trigger is the exact blocker plus what the person can still do.** The trigger
 *    mirrors the D12 entitlement path the application's own surface uses
 *    (`apps/web/src/lib/entitlement-path.ts`): the exact limitation, the requested configuration
 *    preserved, the removal of the paid configuration always possible, the upgrade offered **once**
 *    and a fresh review after any entitlement change — with the paid path present only where the
 *    registered adapter permits it (B12, MCP-AC-030).
 *
 * The commerce posture and the purchase surface live here rather than in the adapter layer because
 * they are facts a *response* carries: the adapter decides, the answer reports. `packages/mcp`
 * projects them (`./billing.ts`); nothing in this file selects a policy.
 */

/* ------------------------------- the posture ------------------------------- */

/**
 * Whether a registered distribution permits hosted purchase initiation. `allowed` is the only value
 * under which a purchase or upgrade surface may appear anywhere — a tool, an error or a deep link.
 */
export const MCP_COMMERCE_POSTURES = [
  'allowed',
  'withheld_by_published_policy',
  'withheld_pending_directory_review',
] as const
export const McpCommercePosture = z.enum(MCP_COMMERCE_POSTURES)
export type McpCommercePosture = z.infer<typeof McpCommercePosture>

/** The stable, link-free in-entitlement actions a withheld commerce posture may name (B21.4.4). */
export const MCP_COMMERCE_ALTERNATIVES = [
  'continue_in_the_app',
  'ask_the_person_to_open_their_account',
] as const

/**
 * Why a repurchase surface is or is not shown, as one value.
 *
 * `SUPPRESSED_LIFETIME_ACTIVE` is PUR-15 stated as data: an account that already holds Lifetime is
 * never offered another one, on any host, whatever the adapter's posture.
 */
export const MCP_REPURCHASE_PROMPTS = [
  'SUPPRESSED_LIFETIME_ACTIVE',
  'SUPPRESSED_HOST_POLICY',
  'PERMITTED_ON_POLICY_SURFACES',
] as const
export const McpRepurchasePrompt = z.enum(MCP_REPURCHASE_PROMPTS)
export type McpRepurchasePrompt = z.infer<typeof McpRepurchasePrompt>

export const McpPurchaseSurface = z
  .object({
    /** The adapter's posture, never the account's state: an account fact is `prompt`'s business. */
    adapter_permits_initiation: z.boolean(),
    commerce_posture: McpCommercePosture,
    prompt: McpRepurchasePrompt,
    /** The in-entitlement actions to name instead, when the posture withholds commerce. */
    allowed_alternatives: z.array(z.string().min(1)),
    /** The registry's neutral explanation key, or `null` when the posture permits initiation. */
    explanation_key: z.string().min(1).max(160).nullable(),
  })
  .strict()
export type McpPurchaseSurface = z.infer<typeof McpPurchaseSurface>

/* ------------------------------ purchases read ----------------------------- */

/** The most purchases one page returns. An account's verified purchases are few; the bound is a rule. */
export const MCP_PURCHASE_MAX_PAGE = 100

/**
 * One verified purchase as the account may see it (B13.1). It is the account's own export shape
 * (`AccountExportPurchase`) minus the provider's transaction locator and plus the two facts a
 * recovery answer needs: what this purchase granted and whether that grant is live now.
 *
 * The provider transaction id is deliberately absent: the claim/reconcile path takes it as a
 * *reauthenticated session* locator (S6.3) and is never an MCP tool, so a model has no use for it —
 * and a host that does not hold it cannot be talked into presenting it.
 */
export const McpPurchaseRecord = z
  .object({
    /** The account's own reference for the purchase — the cursor key, and what support asks for. */
    purchase_ref: z.string().min(1).max(128),
    provider: PurchaseProvider,
    environment: z.enum(['sandbox', 'production']),
    product_id: z.string().min(1).max(128),
    purchase_type: PurchaseType,
    state: z.enum(['pending', 'purchased', 'restored', 'revoked', 'failed']),
    verification_state: z.enum(['unverified', 'verified', 'failed']),
    /** The credits this purchase granted, and whether that entitlement contributes right now. */
    granted_credits: z.number().int().nonnegative(),
    entitlement_live: z.boolean(),
    purchased_at: z.string().datetime().nullable(),
    verified_at: z.string().datetime().nullable(),
    revoked_at: z.string().datetime().nullable(),
  })
  .strict()
export type McpPurchaseRecord = z.infer<typeof McpPurchaseRecord>

export const getPurchasesInput = z
  .object({
    cursor: z.string().min(1).max(512).optional(),
    limit: z.number().int().positive().max(MCP_PURCHASE_MAX_PAGE).default(50),
  })
  .strict()
export type GetPurchasesInput = z.infer<typeof getPurchasesInput>

export const McpPurchasesResult = z
  .object({
    purchases: z.array(McpPurchaseRecord),
    next_cursor: z.string().min(1).max(512).nullable(),
    /** The same authoritative snapshot `/v1/me`, Restore and Verify answer (§2.9). */
    entitlement: MeEntitlementSnapshot,
    lifetime_active: z.boolean(),
    purchase_surface: McpPurchaseSurface,
    bounds: z.object({ max_page: z.number().int().positive() }).strict(),
  })
  .strict()
export type McpPurchasesResult = z.infer<typeof McpPurchasesResult>

/* -------------------------------- restore ---------------------------------- */

export const restorePurchasesInput = z.object({}).strict()
export type RestorePurchasesInput = z.infer<typeof restorePurchasesInput>

/**
 * The S6.3 restore projection. `reconciliation` is the endpoint's own shape, reused verbatim, so the
 * three literals travel unchanged: a restore re-derives, it never grants (`grants_new_entitlement:
 * false`) and it never touches a local document (`restores_local_documents: false`, PUR-04/ADD-027).
 */
export const McpRestorePurchasesResult = z
  .object({
    restored: z.boolean(),
    entitlement: MeEntitlementSnapshot,
    reconciliation: RestoreReconciliation,
    purchase_surface: McpPurchaseSurface,
  })
  .strict()
export type McpRestorePurchasesResult = z.infer<typeof McpRestorePurchasesResult>

/* -------------------------------- checkout --------------------------------- */

export const startCheckoutInput = z
  .object({
    /** The SKU the person intends to buy. The service owns whether it is on sale (S5.7). */
    product_id: z.string().min(1).max(128),
  })
  .strict()
export type StartCheckoutInput = z.infer<typeof startCheckoutInput>

/**
 * What a permitted hosted checkout answers: the provider's page and its state, the display facts the
 * person sees before they leave, and the two literals that keep the boundary honest.
 *
 * There is no field for a payment credential here, and none anywhere else in this file: the
 * provider's hosted page is the only surface one is ever entered on (B13), and `no contract or
 * request schema in @signbento/contracts declares a card-like field` is asserted over every schema
 * this package exports, this one included.
 */
export const McpCheckoutStartResult = z
  .object({
    session: CheckoutSessionView,
    product: CheckoutProductDisplay,
    /** The provider session's state at the moment the service answered. */
    state: z.literal('OPEN'),
    /** Provider verification — not a redirect — is what grants (MCP-AC-060). */
    entitlement_granted: z.literal(false),
    entitlement_authority: z.literal('PROVIDER_VERIFICATION'),
    /** The one surface a payment credential is ever entered on. */
    payment_credential_surface: z.literal('PROVIDER_HOSTED_PAGE'),
  })
  .strict()
export type McpCheckoutStartResult = z.infer<typeof McpCheckoutStartResult>

/* ----------------------------- upgrade triggers ---------------------------- */

/** The MCP plan vocabulary a blocker is named in — the same one capability discovery reports. */
export const MCP_PLAN_TRIGGER_KINDS = [
  'EXTERNAL_SIGNER_LIMIT',
  'CC_RECIPIENTS',
  'SIGNING_DAYS',
  'ACTIVE_REQUEST_LIMIT',
  'ATTACHMENT_REQUEST',
  'DELIVERY_CREDIT',
] as const
export const McpPlanTriggerKind = z.enum(MCP_PLAN_TRIGGER_KINDS)
export type McpPlanTriggerKind = z.infer<typeof McpPlanTriggerKind>

/**
 * The exact blocker. `message_key` is the register's own limitation key
 * (`limitation.external_signers:2/1`), parsed by the host into the numbers below, so "the exact
 * blocker" is the same machine fact the application's own surface renders — and the refusal's own
 * `code` stays the application's, so one state never acquires a second name on the host surface.
 */
export const McpPlanBlocker = z
  .object({
    kind: McpPlanTriggerKind,
    message_key: z.string().min(1).max(160),
    /** How much the configuration asked for, and how much the plan allows. `null` when the */
    /** limitation is not a pair of numbers (an attachment request names a tier, not a count). */
    requested: z.number().int().nonnegative().nullable(),
    allowed: z.number().int().nonnegative().nullable(),
    /** The field the refusal named, so a host can point at the exact input. */
    field_path: z.string().min(1).max(120),
  })
  .strict()
export type McpPlanBlocker = z.infer<typeof McpPlanBlocker>

/**
 * The configuration the host asked for, preserved. This is what makes "no re-upload/re-entry"
 * structural: after the entitlement changes, the host re-sends exactly these values and nothing is
 * asked of the person again (MCP-AC-028/029).
 *
 * It carries what the *request* carried, because that is what a host can re-send: the action
 * reference, the source item and its revision, the participant counts and the requested settings.
 * The source digest is not here — the service re-derives it from the same item + revision, and the
 * review that follows binds it — so a preserved block can never be mistaken for a content copy.
 */
export const McpPreservedDraft = z
  .object({
    action_ref: z.string().min(1).max(255).nullable(),
    source_item_id: z.string().min(1).max(128).nullable(),
    source_revision: z.number().int().positive().nullable(),
    external_signers: z.number().int().nonnegative(),
    cc_recipients: z.number().int().nonnegative(),
    signing_days: z.number().int().positive().nullable(),
    routing_mode: RoutingMode.nullable(),
    delivery_mode: DeliveryMode.nullable(),
  })
  .strict()
export type McpPreservedDraft = z.infer<typeof McpPreservedDraft>

/**
 * The B12 upgrade-trigger response, attached to the refusal of a plan-dependent action. Its fields
 * are the four things the D12 entitlement path owes the person, in the order it owes them:
 * the exact blocker, the preserved work, the permitted in-entitlement alternative, and — only where
 * the registered adapter permits it — a paid path offered once.
 */
export const McpPlanTrigger = z
  .object({
    blocker: McpPlanBlocker,
    preserved: McpPreservedDraft,
    work_preserved: z.literal(true),
    can_remove_paid_configuration: z.literal(true),
    allowed_alternatives: z.array(z.string().min(1)),
    /** `OFFER_ONCE` only where the adapter permits commerce *and* an upgrade would change the answer. */
    upgrade_path: z.enum(['NONE', 'OFFER_ONCE']),
    /** D12: never repeatedly upsell in the same flow. */
    repeat_upsell_in_same_flow: z.literal(false),
    /** After the entitlement changes, the person reviews again before anything is committed. */
    fresh_review_required_after_change: z.literal(true),
    purchase_surface: McpPurchaseSurface,
  })
  .strict()
export type McpPlanTrigger = z.infer<typeof McpPlanTrigger>
